import { icon_edit, icon_outline_trash } from "@/utils/svgIcons"
import { zodResolver } from "@hookform/resolvers/zod"
import { useState } from "react"
import { useForm } from "react-hook-form"
import { z } from "zod"
import { ConfirmDialog } from "../ui/confirmDialog"

interface InlineEditablePropertyProps {
   label: string
   value: string | null | undefined
   field: string
   editable?: boolean
   schema?: z.ZodType<any>
   onSave?: (field: string, value: string) => Promise<void> | void
   type?: "text" | "select" | "date" | "time"
   options?: string[]
   onDelete?: () => void
   formatDisplayedValue?: (value: string | null | undefined) => string
}

export function InlineEditableProperty({
   label,
   value,
   field,
   editable = true,
   schema,
   onSave,
   type = "text",
   options,
   onDelete,
   formatDisplayedValue,
}: InlineEditablePropertyProps) {
   const [isEditing, setIsEditing] = useState(false)
   const [showConfirmDelete, setShowConfirmDelete] = useState(false)

   const formSchema = schema ? z.object({ [field]: schema }) : undefined

   const isDeletable = onDelete !== undefined

   const {
      register,
      handleSubmit,
      formState: { errors, isValid },
      reset,
   } = useForm({
      resolver: formSchema ? zodResolver(formSchema) : undefined,
      defaultValues: {
         [field]: value || "",
      },
      mode: "all",
      reValidateMode: "onChange",
   })

   const handleEdit = () => {
      setIsEditing(true)

      // Give focus to the input
      setTimeout(() => {
         const inputElement = document.querySelector<HTMLInputElement>(`[name="${field}"]`)
         if (inputElement) {
            inputElement.focus()
         }
      }, 50)

      reset({ [field]: value || "" })
   }

   const handleCancel = () => {
      setIsEditing(false)
      reset({ [field]: value || "" })
   }

   const onSubmit = async (data: any) => {
      try {
         if (onSave) {
            await onSave(field, data[field])
         }
         setIsEditing(false)
      } catch (error) {
         console.error("Error saving:", error)
         throw error
      }
   }

   return (
      <div className="form-control w-full">
         {label && (
            <label className="label">
               <span className="label-text">{label}</span>
            </label>
         )}
         <div className="relative">
            {isEditing ? (
               <form onSubmit={handleSubmit(onSubmit)} className="flex space-x-2">
                  <div className="flex-grow">
                     {type === "select" ? (
                        <select
                           className={`select select-bordered w-full ${
                              errors[field] ? "select-error" : ""
                           }`}
                           {...register(field)}
                        >
                           {options?.map((option) => (
                              <option key={option} value={option}>
                                 {option}
                              </option>
                           ))}
                        </select>
                     ) : (
                        <input
                           type={type}
                           className={`input input-bordered w-full ${
                              errors[field] ? "input-error" : ""
                           }`}
                           {...register(field)}
                        />
                     )}
                     {errors[field] && (
                        <div className="text-error my-1">{errors[field]?.message as string}</div>
                     )}
                  </div>
                  <button
                     type="submit"
                     className="btn btn-primary"
                     disabled={formSchema && !isValid}
                  >
                     <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth={1.5}
                        stroke="currentColor"
                        className="w-6 h-6"
                     >
                        <path
                           strokeLinecap="round"
                           strokeLinejoin="round"
                           d="M4.5 12.75l6 6 9-13.5"
                        />
                     </svg>
                  </button>
                  <button type="button" className="btn" onClick={handleCancel}>
                     <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth={1.5}
                        stroke="currentColor"
                        className="w-6 h-6"
                     >
                        <path
                           strokeLinecap="round"
                           strokeLinejoin="round"
                           d="M6 18L18 6M6 6l12 12"
                        />
                     </svg>
                  </button>
               </form>
            ) : (
               <div className="flex justify-between items-center">
                  {!editable && (
                     <span className="py-3 px-8">
                        {formatDisplayedValue ? formatDisplayedValue(value) : value || ""}
                     </span>
                  )}

                  {editable && (
                     <>
                        <button
                           className="btn btn-ghost text-base font-normal flex-grow"
                           onClick={handleEdit}
                        >
                           <span className="w-full py-3 px-4 text-left">
                              {formatDisplayedValue ? formatDisplayedValue(value) : value || ""}
                           </span>
                        </button>

                        <button type="button" className="btn btn-ghost" onClick={handleEdit}>
                           {icon_edit}
                        </button>
                     </>
                  )}

                  {isDeletable && (
                     <>
                        <button
                           className="btn btn-ghost text-error"
                           onClick={() => setShowConfirmDelete(true)}
                        >
                           {icon_outline_trash}
                        </button>
                        {showConfirmDelete && (
                           <ConfirmDialog
                              message="Êtes-vous sûr de vouloir supprimer cet élément ?"
                              onConfirm={() => {
                                 onDelete?.()
                                 setShowConfirmDelete(false)
                              }}
                              onCancel={() => setShowConfirmDelete(false)}
                           />
                        )}
                     </>
                  )}
               </div>
            )}
         </div>
      </div>
   )
}
